import { useQuery } from '@apollo/client';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  endpointPortfolioConstituentBreakdownGET,
  endpointPortfolioCorrelationClusterGET,
  endpointPortfolioForwardProjectionGET,
  endpointPortfolioPerformanceGET,
} from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { AssetBreakdownWidget } from '~/components/ui/widgets/AssetBreakdownWidget/AssetBreakdownWidget';
import { CorrelationClusterWidget } from '~/components/ui/widgets/CorrelationClusterWidget/CorrelationClusterWidget';
import { ForwardReturnsWidget } from '~/components/ui/widgets/ForwardReturnsWidget/ForwardReturnsWidget';
import { HistoricalReturnsWidget } from '~/components/ui/widgets/HistoricalReturnsWidget/HistoricalReturnsWidget';
import { SectorAllocationWidgetWrapper } from '~/components/ui/widgets/SectorAllocationWidget/SectorAllocationWidgetWrapper';
import { ThemeManager } from '~/components/ui/theme/ThemeManager';
import {
  GET_LATEST_SNAPSHOT_ID,
  GET_PORTFOLIO_NAME,
} from '~/graphql/queries/portfolio';
import { useHasBenchmark } from '~/hooks/useHasBenchmark';

type SearchParams = {
  portfolioId?: string;
  snapshotId?: string;
  scenario?: 'PROPOSED' | 'CURRENT_HOLDINGS' | 'BENCHMARK';
  benchmarkName?: string;
  title?: string;
  horizonDays?: number;
  lookbackDays?: number;
  correlationThreshold?: number;
  portfolioType?: 'current' | 'proposed';
  metricType?: 'drawdown' | 'beta';
  portfolioName?: string;
};

export const Route = createFileRoute('/_authLayout/popout/$widgetType')({
  component: PopoutWidget,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      portfolioId: search.portfolioId as string | undefined,
      snapshotId: search.snapshotId as string | undefined,
      scenario:
        (search.scenario as 'PROPOSED' | 'CURRENT_HOLDINGS' | 'BENCHMARK') ||
        'PROPOSED',
      benchmarkName: search.benchmarkName as string | undefined,
      title: search.title as string | undefined,
      horizonDays: search.horizonDays ? Number(search.horizonDays) : undefined,
      lookbackDays: search.lookbackDays
        ? Number(search.lookbackDays)
        : undefined,
      correlationThreshold: search.correlationThreshold
        ? Number(search.correlationThreshold)
        : undefined,
      portfolioType: search.portfolioType as 'current' | 'proposed' | undefined,
      metricType: search.metricType as 'drawdown' | 'beta' | undefined,
      portfolioName: search.portfolioName as string | undefined,
    };
  },
});

function PopoutWidget() {
  const { widgetType } = useParams({ from: '/_authLayout/popout/$widgetType' });
  const {
    portfolioId,
    snapshotId,
    scenario = 'PROPOSED',
    benchmarkName,
    title,
    horizonDays,
    lookbackDays,
    correlationThreshold,
    portfolioType,
    metricType,
    portfolioName,
  } = useSearch({
    from: '/_authLayout/popout/$widgetType',
  });

  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [actualSnapshotId, setActualSnapshotId] = useState<string | null>(null);

  // Check if portfolio has a benchmark using the dedicated endpoint
  const { hasBenchmark, isLoading: hasBenchmarkLoading } =
    useHasBenchmark(portfolioId);

  const { data: snapshotData } = useQuery(GET_LATEST_SNAPSHOT_ID, {
    variables: { portfolioId },
    skip: !portfolioId || !!snapshotId,
  });

  // Fetch portfolio name if not provided via URL
  const { data: portfolioData } = useQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId || !!portfolioName,
  });

  const displayPortfolioName =
    portfolioName || portfolioData?.EQPortfolio?.[0]?.name;

  const fetchData = useCallback(async () => {
    if (!portfolioId && !snapshotId) {
      setError(new Error('Missing portfolioId or snapshotId'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let targetSnapshotId = snapshotId;
      if (
        !targetSnapshotId &&
        snapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id
      ) {
        targetSnapshotId =
          snapshotData.EQPortfolio[0].EQPortfolioSnapshots[0].id;
      }

      if (!targetSnapshotId) {
        throw new Error('No snapshot ID available');
      }

      setActualSnapshotId(targetSnapshotId);

      if (widgetType === 'forward-returns') {
        const response = await endpointPortfolioForwardProjectionGET({
          snapshot_id: targetSnapshotId,
          include_current: true,
          include_benchmark: hasBenchmark,
        });
        setData(response);
      } else if (widgetType === 'asset-breakdown') {
        const response = await endpointPortfolioConstituentBreakdownGET({
          snapshot_id: targetSnapshotId,
          scenario: scenario.toLowerCase() as
            | 'proposed'
            | 'current_holdings'
            | 'benchmark',
          horizon_days: horizonDays || 63,
        });
        setData(response);
      } else if (widgetType === 'correlation-cluster') {
        const response = await endpointPortfolioCorrelationClusterGET(
          targetSnapshotId,
          {
            lookbackDays: lookbackDays || 252,
            correlationThreshold: correlationThreshold || 0.85,
            portfolioType: portfolioType || 'proposed',
          },
        );
        setData(response);
      } else if (widgetType === 'historical-returns') {
        // Calculate YTD window - if within first 3 months of year, include previous year
        const endDate = new Date();
        const startDate = new Date();
        const currentMonth = endDate.getMonth(); // 0-indexed (0 = Jan, 1 = Feb, 2 = Mar)
        const startYear =
          currentMonth < 3 ? endDate.getFullYear() - 1 : endDate.getFullYear();
        startDate.setFullYear(startYear, 0, 1); // January 1st of the calculated year

        const response = await endpointPortfolioPerformanceGET({
          snapshot_id: targetSnapshotId,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          include_current: true,
          include_benchmark: hasBenchmark,
        });
        setData(response);
      } else if (widgetType === 'sector-allocation') {
        // SectorAllocationWidgetWrapper fetches its own data
        // Just mark as loaded
        setData(null);
      } else {
        throw new Error(`Unknown widget type: ${widgetType}`);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [
    snapshotId,
    portfolioId,
    snapshotData,
    widgetType,
    scenario,
    horizonDays,
    lookbackDays,
    correlationThreshold,
    portfolioType,
    hasBenchmark,
  ]);

  useEffect(() => {
    // Wait for benchmark check to complete before fetching data
    if (hasBenchmarkLoading) {
      return;
    }

    if (portfolioId || snapshotId) {
      fetchData();
    }
  }, [portfolioId, snapshotId, snapshotData, fetchData, hasBenchmarkLoading]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (title) {
      document.title = title;
    } else if (widgetType) {
      document.title = widgetType
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }
  }, [title, widgetType]);

  if (error && !isLoading) {
    return (
      <>
        <ThemeManager />
        <div
          style={{
            height: '100vh',
            width: '100%',
            backgroundColor: 'var(--bq-surface-body)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--bq-text-primary)',
            padding: '20px',
          }}
        >
          <h2 style={{ color: 'var(--bq-color-error)', marginBottom: '16px' }}>
            Error
          </h2>
          <p style={{ color: 'var(--bq-text-secondary)' }}>{error.message}</p>
          <button
            onClick={refetch}
            style={{
              marginTop: '20px',
              padding: '8px 16px',
              backgroundColor: 'var(--bq-color-primary)',
              color: 'var(--bq-text-inverse)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <ThemeManager />
      <div
        style={{
          height: '100vh',
          width: '100%',
          backgroundColor: 'var(--bq-surface-body)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Portfolio Header */}
        {displayPortfolioName && (
          <div
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--bq-surface-card)',
              borderBottom: '1px solid var(--bq-border-strong)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            <i
              className="pi pi-briefcase"
              style={{ color: 'var(--bq-text-secondary)', fontSize: '14px' }}
            />
            <span
              style={{ color: 'var(--bq-text-secondary)', fontSize: '13px' }}
            >
              Portfolio:
            </span>
            <span
              style={{
                color: 'var(--bq-text-primary)',
                fontSize: '13px',
                fontWeight: 500,
              }}
            >
              {displayPortfolioName}
            </span>
          </div>
        )}

        {/* Widget Content */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {widgetType === 'forward-returns' && (
            <ForwardReturnsWidget
              data={data}
              isLoading={isLoading || hasBenchmarkLoading}
              error={error}
              refetch={refetch}
              widgetId={`popout-${widgetType}`}
              title={title || 'Forward & Real Returns'}
              snapshotId={actualSnapshotId}
              benchmarkName={benchmarkName || 'Benchmark'}
            />
          )}
          {widgetType === 'asset-breakdown' && (
            <AssetBreakdownWidget
              data={data}
              isLoading={isLoading}
              error={error}
              refetch={refetch}
              widgetId={`popout-${widgetType}`}
              title={title || 'Forward Returns Breakdown'}
              snapshotId={actualSnapshotId}
              scenario={scenario}
              horizonDays={horizonDays}
            />
          )}
          {widgetType === 'correlation-cluster' && actualSnapshotId && (
            <CorrelationClusterWidget
              data={data}
              isLoading={isLoading}
              error={error}
              refetch={refetch}
              widgetId={`popout-${widgetType}`}
              title={title || 'Correlation Cluster'}
              snapshotId={actualSnapshotId}
              lookbackDays={lookbackDays || 252}
              correlationThreshold={correlationThreshold || 0.85}
              initialPortfolioType={portfolioType || 'proposed'}
              initialMetricType={metricType || 'drawdown'}
            />
          )}
          {widgetType === 'historical-returns' && (
            <HistoricalReturnsWidget
              data={data}
              isLoading={isLoading || hasBenchmarkLoading}
              error={error}
              refetch={refetch}
              widgetId={`popout-${widgetType}`}
              title={title || 'Historical Returns'}
              snapshotId={actualSnapshotId}
              benchmarkName={benchmarkName || 'Benchmark'}
            />
          )}
          {widgetType === 'sector-allocation' && (
            <SectorAllocationWidgetWrapper
              widgetId={`popout-${widgetType}`}
              title={title || 'Allocation Analysis'}
              snapshotId={actualSnapshotId}
            />
          )}
        </div>
      </div>
    </>
  );
}
